import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell } from '../layout/AppShell.js';
import { RequireAuth, RequireRole, RequirePlatformAdmin } from './guards.js';
import { useOnboarding } from '../features/onboarding/hooks.js';

/**
 * RR v6 router skeleton. Public /login; everything else behind RequireAuth in
 * the AppShell. Role-restricted groups use RequireRole; the /admin area uses
 * RequirePlatformAdmin (Module 12). Every feature page is lazy-imported.
 */

// Lazy feature pages (each a Phase 0 stub; tracks replace them).
const LoginPage = lazy(() => import('../features/auth/LoginPage.js'));
const DashboardPage = lazy(() => import('../features/analytics/DashboardPage.js'));
const OnboardingPage = lazy(() => import('../features/onboarding/OnboardingPage.js'));
const OffersPage = lazy(() => import('../features/offers/OffersPage.js'));
const CataloguePage = lazy(() => import('../features/catalogue/CataloguePage.js'));
const MerchantMapPage = lazy(() => import('../features/merchant-map/MerchantMapPage.js'));
const DistributionPage = lazy(() => import('../features/distribution/DistributionPage.js'));
const TrackingPage = lazy(() => import('../features/tracking/TrackingPage.js'));
const BillingPage = lazy(() => import('../features/billing/BillingPage.js'));
const ConversionsPage = lazy(() => import('../features/billing/ConversionsPage.js'));
const LegalPage = lazy(() => import('../features/legal/LegalPage.js'));
const SettingsPage = lazy(() => import('../features/settings/SettingsPage.js'));
const AdminPage = lazy(() => import('../features/admin/AdminPage.js'));
const AdminSurfacesPage = lazy(() => import('../features/admin/AdminSurfacesPage.js'));

function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<div style={{ padding: 48 }}>Loading…</div>}>{children}</Suspense>;
}

/**
 * Redirects to /onboarding when onboarding is incomplete, otherwise /analytics.
 * Runs inside RequireAuth so the session + query client are available.
 */
function RootRedirect() {
  const { data, isLoading } = useOnboarding();
  if (isLoading) return <div style={{ padding: 48, color: '#6B6B66' }}>Loading…</div>;
  const done =data?.steps.every((s) => s.status === 'done') ?? false;
  return <Navigate to={done ? '/analytics' : '/onboarding'} replace />;
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <Lazy>
        <LoginPage />
      </Lazy>
    ),
  },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <RootRedirect /> },
          {
            path: 'analytics',
            element: (
              <Lazy>
                <DashboardPage />
              </Lazy>
            ),
          },
          {
            path: 'onboarding',
            element: (
              <Lazy>
                <OnboardingPage />
              </Lazy>
            ),
          },
          {
            path: 'legal',
            element: (
              <Lazy>
                <LegalPage />
              </Lazy>
            ),
          },
          {
            path: 'settings',
            element: (
              <Lazy>
                <SettingsPage />
              </Lazy>
            ),
          },

          // Editor+ group (offers/catalogue/map/distribution/tracking)
          {
            element: <RequireRole allowed={['owner', 'admin', 'editor']} />,
            children: [
              {
                path: 'offers',
                element: (
                  <Lazy>
                    <OffersPage />
                  </Lazy>
                ),
              },
              {
                path: 'catalogue',
                element: (
                  <Lazy>
                    <CataloguePage />
                  </Lazy>
                ),
              },
              {
                path: 'merchant-map',
                element: (
                  <Lazy>
                    <MerchantMapPage />
                  </Lazy>
                ),
              },
              {
                path: 'distribution',
                element: (
                  <Lazy>
                    <DistributionPage />
                  </Lazy>
                ),
              },
              {
                path: 'tracking',
                element: (
                  <Lazy>
                    <TrackingPage />
                  </Lazy>
                ),
              },
            ],
          },

          // Finance/Owner/Admin group (billing)
          {
            element: <RequireRole allowed={['owner', 'admin', 'finance']} />,
            children: [
              {
                path: 'billing',
                element: (
                  <Lazy>
                    <BillingPage />
                  </Lazy>
                ),
              },
              {
                path: 'billing/conversions',
                element: (
                  <Lazy>
                    <ConversionsPage />
                  </Lazy>
                ),
              },
            ],
          },

          // Platform-admin area (Module 12)
          {
            element: <RequirePlatformAdmin />,
            children: [
              {
                path: 'admin',
                element: (
                  <Lazy>
                    <AdminPage />
                  </Lazy>
                ),
              },
              {
                path: 'admin/surfaces',
                element: (
                  <Lazy>
                    <AdminSurfacesPage />
                  </Lazy>
                ),
              },
            ],
          },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/analytics" replace /> },
]);
